import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const PortfolioInsightsFinancials = ({ holdings, baseCurrency, financialData = [] }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const symbols = [...new Set(holdings.map(h => h.symbol))];

    const getHoldingData = (symbol) => {
        const holding = holdings.find(h => h.symbol === symbol);
        return { pe: holding?.pe || null, eps: holding?.eps || null };
    };

    const getFinancialForSymbol = (symbol) => {
        return financialData.find(d => d.symbol === symbol);
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '--';
        const absValue = Math.abs(value);
        if (absValue >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
        if (absValue >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
        if (absValue >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
        return `$${value.toFixed(2)}`;
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return '--';
        return `${value.toFixed(1)}%`;
    };

    const formatGrowth = (value) => {
        if (value === null || value === undefined) return '--';
        const percent = value * 100;
        const sign = percent >= 0 ? '+' : '';
        return `${sign}${percent.toFixed(1)}%`;
    };

    const getGrowthColor = (value) => {
        if (value === null || value === undefined) return theme.palette.text.secondary;
        if (value > 0) return theme.palette.success.main;
        if (value < 0) return theme.palette.error.main;
        return theme.palette.text.secondary;
    };

    return (
        <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: isMobile ? 550 : 'auto' }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Symbol</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                        <TableCell align="right">EPS</TableCell>
                        <TableCell align="right">P/E Ratio</TableCell>
                        <TableCell align="right">Profit Margin</TableCell>
                        <TableCell align="right">Rev Growth</TableCell>
                        <TableCell align="right">EPS Growth</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {symbols.map(symbol => {
                        const { pe, eps } = getHoldingData(symbol);
                        const financial = getFinancialForSymbol(symbol);

                        return (
                            <TableRow key={symbol}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                        {symbol}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {financial ? formatCurrency(financial.revenue) : '--'}
                                </TableCell>
                                <TableCell align="right">
                                    {eps ? `$${eps.toFixed(2)}` : '--'}
                                </TableCell>
                                <TableCell align="right">
                                    {pe ? pe.toFixed(1) : '--'}
                                </TableCell>
                                <TableCell align="right">
                                    {formatPercent(financial?.profitMargin)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: getGrowthColor(financial?.growthRevenue) }}>
                                    {financial ? formatGrowth(financial.growthRevenue) : '--'}
                                </TableCell>
                                <TableCell align="right" sx={{ color: getGrowthColor(financial?.growthEPS) }}>
                                    {financial ? formatGrowth(financial.growthEPS) : '--'}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default PortfolioInsightsFinancials;